import React, { useState, useRef, useEffect } from "react";
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";
import { X, ChevronLeft, ChevronRight, Download } from "lucide-react";
import { CachedImg } from "@/lib/mediaCache";
import { toast } from "sonner";

export async function downloadMedia(url, name) {
  try {
    const res = await fetch(url);
    const blob = await res.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = name || "royalchat-media";
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(a.href), 4000);
    toast.success("Downloaded");
  } catch {
    toast.error("Download failed");
  }
}

export default function MediaViewer({ items, startIndex = 0, onClose }) {
  const [idx, setIdx] = useState(startIndex);
  const [scale, setScale] = useState(1);
  const touch = useRef(null);
  const item = items[idx];
  const many = items.length > 1;
  const go = (d) => setIdx((i) => (i + d + items.length) % items.length);

  // reset zoom tracking when the image changes
  useEffect(() => { setScale(1); }, [idx]);

  const onTouchStart = (e) => {
    if (e.touches.length !== 1) { touch.current = null; return; }
    const t = e.touches[0];
    touch.current = { x: t.clientX, y: t.clientY, t: Date.now() };
  };
  const onTouchEnd = (e) => {
    if (!touch.current || scale > 1.05 || !many) { touch.current = null; return; }
    const t = e.changedTouches[0];
    const dx = t.clientX - touch.current.x, dy = t.clientY - touch.current.y;
    touch.current = null;
    if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.3) go(dx < 0 ? 1 : -1);
  };

  if (!item) return null;

  return (
    <div className="fixed inset-0 z-[100] bg-black/95 flex flex-col" data-testid="media-viewer">
      <div className="flex items-center justify-between px-4 py-3 text-white/90">
        <span className="text-sm truncate max-w-[60%]">{item.name || (item.type === "video" ? "Video" : "Photo")}{many ? ` · ${idx + 1}/${items.length}` : ""}</span>
        <div className="flex items-center gap-2">
          <button onClick={() => downloadMedia(item.url, item.name)} data-testid="media-download-btn"
            className="h-10 w-10 grid place-items-center rounded-full bg-white/10 hover:bg-white/20 text-white"><Download size={20} /></button>
          <button onClick={onClose} data-testid="media-viewer-close"
            className="h-10 w-10 grid place-items-center rounded-full bg-white/10 hover:bg-white/20 text-white"><X size={22} /></button>
        </div>
      </div>

      <div className="flex-1 relative min-h-0" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd} data-testid="media-swipe-area">
        {item.type === "video" ? (
          <div className="h-full grid place-items-center p-2">
            <video key={item.url} src={item.url} controls autoPlay playsInline className="max-h-full max-w-full rounded-xl" data-testid="media-viewer-video" />
          </div>
        ) : (
          <TransformWrapper key={item.url} doubleClick={{ mode: "toggle", step: 2 }} minScale={1} maxScale={5} centerOnInit
            onTransformed={(_ref, state) => setScale(state.scale)}>
            <TransformComponent wrapperClass="!w-full !h-full" contentClass="!w-full !h-full">
              <div className="w-full h-full grid place-items-center">
                <CachedImg src={item.url} alt="" className="max-h-[82vh] max-w-full object-contain select-none" draggable={false} data-testid="media-viewer-image" />
              </div>
            </TransformComponent>
          </TransformWrapper>
        )}

        {many && (
          <>
            <button onClick={() => go(-1)} data-testid="media-prev"
              className="absolute left-3 top-1/2 -translate-y-1/2 h-11 w-11 grid place-items-center rounded-full bg-white/10 hover:bg-white/20 text-white"><ChevronLeft size={24} /></button>
            <button onClick={() => go(1)} data-testid="media-next"
              className="absolute right-3 top-1/2 -translate-y-1/2 h-11 w-11 grid place-items-center rounded-full bg-white/10 hover:bg-white/20 text-white"><ChevronRight size={24} /></button>
          </>
        )}
      </div>

      {items.length > 1 && (
        <div className="flex gap-2 overflow-x-auto no-scrollbar px-4 py-3">
          {items.map((it, i) => (
            <button key={i} onClick={() => setIdx(i)} data-testid={`media-thumb-${i}`}
              className={`shrink-0 h-14 w-14 rounded-lg overflow-hidden border-2 ${i === idx ? "border-[var(--rc-gold)]" : "border-transparent opacity-60"}`}>
              {it.type === "video"
                ? <div className="w-full h-full grid place-items-center bg-white/10 text-white text-lg">▶</div>
                : <img src={it.url} alt="" className="w-full h-full object-cover" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
